import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useMemo, useReducer, useRef } from "react";
import EnhancedTable from "components/enhancedTable/EnhancedTable";
import { isLocalDev } from "utils/environment";
import { LensConfigurationService } from "service/validDataService";
import useAbortController from "hooks/useAbortController";
import useDialogModal from "hooks/useDialogModal";
import ConfirmationDialog from "components/ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "utils/validLookup";
import ValidLensModelInputForm from "./validLensModelInputForm";
import EditValidLensModelForm from "./editValidLensModelForm";
import { makeValidLensModelColumns } from "./columns";
import useLensTypeCatalog from "hooks/useLensTypeCatalog";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { DEFAULT_PAGE_SIZE } from "../../../../utils";

export default function ValidLensModel() {
    const { lensTypeLabels } = useLensTypeCatalog();
    const columns = useMemo(() => makeValidLensModelColumns(lensTypeLabels), [lensTypeLabels]);
    const [ValidLensModelInputDialog, showValidLensModelInputForm] = useDialogModal(ValidLensModelInputForm);
    const [ValidLensModelEditDialog, showValidLensModelEditForm] = useDialogModal(EditValidLensModelForm);

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal] = useAbortController();
    const lastQueryRef = useRef({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0, filters: [], sortBy: [] });
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showValidLensModelInputForm();
    }

    const handleRowEdit = (rowData) => {
        showValidLensModelEditForm(null, { recordToEdit: rowData });
    }

    const handleRowCopy = (rowData) => {
        showValidLensModelEditForm(null, { recordToEdit: rowData, isCopy: true });
    }

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        lastQueryRef.current = { pageSize, pageIndex, filters, sortBy };
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        LensConfigurationService.getModelsWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.rows,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [signal]);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await LensConfigurationService.deleteModel(rowids.map(x => parseInt(x, 10)));
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const recordAddHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    const recordEditHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    return (
        <Box>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Configuration for Lens Models</Typography>
            <Divider sx={{ mb: 2 }} />
            <EnhancedTable
                columns={columns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                    {
                        icon: <ContentCopy />,
                        onClick: handleRowCopy
                    },
                ]}
            />
            <ValidLensModelInputDialog onRecordAdded={recordAddHandler} />
            <ValidLensModelEditDialog onRecordEdit={recordEditHandler} />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    );
}